import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import StandardProgramDetailTemplate from "../Courses/InformationTechnology/ITPrograms/StandardProgramDetailTemplate";
import CustomApplicationsTemplate from "../Courses/InformationTechnology/ITPrograms/CustomApplicationsTemplate";
import CustomAdobeTemplate from "../Courses/InformationTechnology/ITPrograms/CustomAdobeTemplate";
import CyberSecurityTemplate from "./CyberSecurityTemplate";
import ComputerScienceTemplate from "./ComputerScienceTemplate";
import Navbar from "../sub-comp/Navbar";
import Footer from "../sub-comp/MainFooter";
import cmsApi from "../../services/cmsApi";

const UniversalProgramDetailPage = ({ courseCategory }) => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [programData, setProgramData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchProgram = async () => {
      // Extract slug from params or pathname
      let programSlug = slug;
      
      // If no slug in params, extract from pathname
      if (!programSlug) {
        const pathParts = location.pathname.split('/');
        // Try to find slug after course category
        const categoryIndex = pathParts.findIndex(part => 
          ['cyber-security', 'business-administration', 'travel-tourism-management', 'short-courses', 'computer-science', 'computer-engineering', 'information-technology'].includes(part)
        );
        if (categoryIndex !== -1 && pathParts[categoryIndex + 1]) {
          programSlug = pathParts[categoryIndex + 1];
        }
      }

      if (!programSlug) {
        setError("Program slug is required");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const result = await cmsApi.getProgramBySlug(programSlug);

        if (result.success && result.program) {
          const program = result.program;
          
          // Parse JSON fields safely before setting state
          const parsedProgram = {
            ...program,
            // Parse JSON fields safely
            learningItems: program.learningItems 
              ? (typeof program.learningItems === 'string' 
                  ? JSON.parse(program.learningItems) 
                  : program.learningItems)
              : [],
            modules: program.modules 
              ? (typeof program.modules === 'string' 
                  ? JSON.parse(program.modules) 
                  : program.modules)
              : [],
            careerOpportunitiesJson: program.careerOpportunitiesJson 
              ? (typeof program.careerOpportunitiesJson === 'string' 
                  ? JSON.parse(program.careerOpportunitiesJson) 
                  : program.careerOpportunitiesJson)
              : [],
            customContent: program.customContent 
              ? (typeof program.customContent === 'string' 
                  ? JSON.parse(program.customContent) 
                  : program.customContent)
              : null,
            coreConcepts: program.coreConcepts
              ? (typeof program.coreConcepts === 'string'
                  ? JSON.parse(program.coreConcepts)
                  : program.coreConcepts)
              : [],
            learningPath: program.learningPath
              ? (typeof program.learningPath === 'string'
                  ? JSON.parse(program.learningPath)
                  : program.learningPath)
              : [],
            // Parse new custom layout fields
            heroApplications: program.heroApplications
              ? (typeof program.heroApplications === 'string'
                  ? JSON.parse(program.heroApplications)
                  : program.heroApplications)
              : null,
            applicationCards: program.applicationCards
              ? (typeof program.applicationCards === 'string'
                  ? JSON.parse(program.applicationCards)
                  : program.applicationCards)
              : null,
            learningLevels: program.learningLevels
              ? (typeof program.learningLevels === 'string'
                  ? JSON.parse(program.learningLevels)
                  : program.learningLevels)
              : null,
            // Parse Cyber Security fields
            introParagraphs: program.introParagraphs
              ? (typeof program.introParagraphs === 'string'
                  ? JSON.parse(program.introParagraphs)
                  : program.introParagraphs)
              : null,
            classroomParagraphs: program.classroomParagraphs
              ? (typeof program.classroomParagraphs === 'string'
                  ? JSON.parse(program.classroomParagraphs)
                  : program.classroomParagraphs)
              : null,
            beyondClassroomParagraphs: program.beyondClassroomParagraphs
              ? (typeof program.beyondClassroomParagraphs === 'string'
                  ? JSON.parse(program.beyondClassroomParagraphs)
                  : program.beyondClassroomParagraphs)
              : null,
            differenceParagraphs: program.differenceParagraphs
              ? (typeof program.differenceParagraphs === 'string'
                  ? JSON.parse(program.differenceParagraphs)
                  : program.differenceParagraphs)
              : null,
            // Parse Computer Science/Engineering fields
            learningOutcomes: program.learningOutcomes
              ? (typeof program.learningOutcomes === 'string'
                  ? JSON.parse(program.learningOutcomes)
                  : program.learningOutcomes)
              : null,
            careerPaths: program.careerPaths
              ? (typeof program.careerPaths === 'string'
                  ? JSON.parse(program.careerPaths)
                  : program.careerPaths)
              : null,
            // These are already strings, no parsing needed
            suiteTitle: program.suiteTitle || null,
            suiteDescription: program.suiteDescription || null,
            learningPathTitle: program.learningPathTitle || null,
            learningPathDesc: program.learningPathDesc || null,
          };
          
          setProgramData(parsedProgram);
        } else {
          console.error('Frontend: Failed to fetch program:', result.error);
          setError(result.error || "Program not found");
        }
      } catch (err) {
        console.error("Error fetching program:", err);
        setError(err.message || "Failed to load program");
      } finally {
        setLoading(false);
      }
    };

    fetchProgram();
  }, [slug, location.pathname, courseCategory]);

  // Loading state
  if (loading) {
    return (
      <div>
        <Navbar />
        <div className="mid-desktop-page">
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-custom-pink border-t-transparent mx-auto mb-4"></div>
            <p className="text-gray-600 font-medium">Loading program...</p>
          </div>
        </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div>
        <Navbar />
        <div className="mid-desktop-page">
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <h1 className="text-3xl font-bold text-gray-800 mb-4">Error Loading Program</h1>
            <p className="text-gray-600 mb-6">{error}</p>
            <button
              onClick={() => navigate(-1)}
              className="px-6 py-3 bg-custom-pink text-white rounded-lg hover:bg-custom-pink/90 transition-colors"
            >
              Go Back
            </button>
          </div>
        </div>
        </div>
        <Footer />
      </div>
    );
  }

  // If we have program data, use dynamic templates
  if (programData) {
    const layout = programData.detailPageLayout || "standard";
    
    console.log('Frontend: Rendering with layout:', layout);
    
    // Use dynamic templates based on layout
    switch (layout) {
      case "cyber-security":
      case "business-administration":
      case "travel-tourism":
      case "short-course":
        return <CyberSecurityTemplate program={programData} />;
      case "computer-science":
      case "computer-engineering":
        return <ComputerScienceTemplate program={programData} />;
      case "custom-applications":
        return <CustomApplicationsTemplate programData={programData} />;
      case "custom-adobe":
        return <CustomAdobeTemplate programData={programData} />;
      case "standard":
      default:
        return <StandardProgramDetailTemplate programData={programData} />;
    }
  }

  // Last resort: Show error
  return (
    <div>
      <Navbar />
      <div className="mid-desktop-page">
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-gray-800 mb-4">Program Not Found</h1>
          <p className="text-gray-600 mb-6">The program you're looking for doesn't exist.</p>
          <button
            onClick={() => navigate(-1)}
            className="px-6 py-3 bg-custom-pink text-white rounded-lg hover:bg-custom-pink/90 transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
      </div>
      <Footer />
    </div>
  );
};

export default UniversalProgramDetailPage;

